import {
    ArrowPathIcon,
    ChevronDownIcon,
    ClockIcon,
    SparklesIcon
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { format } from 'date-fns';
import type { PatientAnalysis, WaitingOn } from '../../../services/analysis';
import { healthTheme, type AnalysisVariant, type HealthTheme } from './healthConfig';
import { ANALYSIS_SOURCES } from './AnalysisStates';
import { MarkdownText } from './MarkdownText';

const RING_RADIUS = 42;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

interface HealthScoreRingProps {
    score: number;
    theme: HealthTheme;
    /** Sizing wrapper classes, e.g. w-[112px] h-[112px]. */
    className: string;
    valueClassName: string;
}

function HealthScoreRing({ score, theme, className, valueClassName }: HealthScoreRingProps) {
    const dash = (score / 100) * RING_CIRCUMFERENCE;
    return (
        <div className={`relative ${className}`}>
            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle
                    cx="50"
                    cy="50"
                    r={RING_RADIUS}
                    stroke={theme.track}
                    strokeWidth="9"
                    fill="none"
                />
                <circle
                    cx="50"
                    cy="50"
                    r={RING_RADIUS}
                    stroke={theme.ring}
                    strokeWidth="9"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${dash} ${RING_CIRCUMFERENCE - dash}`}
                />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`leading-none font-bold text-gray-900 ${valueClassName}`}>
                    {score}
                </span>
                <span className="text-[10px] text-gray-400 mt-0.5">/ 100</span>
            </div>
        </div>
    );
}

function HealthLabelChip({ theme }: { theme: HealthTheme }) {
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[12px] font-semibold ${theme.bg} ${theme.text}`}
        >
            <span className={`w-1.5 h-1.5 rounded-full ${theme.dot}`} />
            {theme.label}
        </span>
    );
}

const SCORE_CAPTION = 'Overall situational score across treatment, compliance & communication';

interface HealthScoreCardProps {
    analysis: PatientAnalysis;
    variant: AnalysisVariant;
}

function HealthScoreCard({ analysis, variant }: HealthScoreCardProps) {
    const theme = healthTheme(analysis.healthLabel);
    const score = analysis.healthScore ?? 0;
    if (variant === 'slim') {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-4 flex items-center gap-4">
                <HealthScoreRing
                    score={score}
                    theme={theme}
                    className="w-[84px] h-[84px] flex-shrink-0"
                    valueClassName="text-[24px]"
                />
                <div className="flex flex-col items-start gap-1.5 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-400">
                        Health score
                    </div>
                    <HealthLabelChip theme={theme} />
                    <div className="text-[11px] text-gray-400 leading-snug">{SCORE_CAPTION}</div>
                </div>
            </div>
        );
    }
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 flex flex-col items-center justify-center text-center">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 self-start">
                Health score
            </div>
            <HealthScoreRing
                score={score}
                theme={theme}
                className="w-[112px] h-[112px] my-2"
                valueClassName="text-[30px]"
            />
            <HealthLabelChip theme={theme} />
            <div className="text-[11px] text-gray-400 mt-2 leading-snug">{SCORE_CAPTION}</div>
        </div>
    );
}

const WAITING_CHIPS: Partial<Record<WaitingOn, { label: string; className: string }>> = {
    doctor: { label: 'Waiting on doctor', className: 'bg-amber-50 text-amber-700' },
    odo: { label: 'Waiting on ODO', className: 'bg-sky-50 text-sky-700' }
};

interface CardProps {
    analysis: PatientAnalysis;
}

function TldrCard({ analysis }: CardProps) {
    const waiting = analysis.result ? WAITING_CHIPS[analysis.result.waiting_on] : undefined;
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 flex flex-col flex-1">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                    <SparklesIcon className="w-3.5 h-3.5 text-gray-400" />
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-400">
                        TL;DR
                    </div>
                </div>
                {waiting && (
                    <span
                        title={analysis.result?.waiting_reason || undefined}
                        className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold ${waiting.className}`}
                    >
                        {waiting.label}
                    </span>
                )}
            </div>
            <div className="flex-1 flex flex-col justify-center">
                <div
                    className="text-[13px] leading-relaxed text-gray-900"
                    style={{ textWrap: 'pretty' }}
                >
                    <MarkdownText text={analysis.result?.tldr ?? ''} />
                </div>
            </div>
        </div>
    );
}

function RecapCard({ items }: { items: string[] }) {
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-3">
                Recap
            </div>
            <ul className="flex flex-col gap-2.5">
                {items.map((text, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                        <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
                        <span className="text-[13px] text-gray-700 leading-snug">
                            <MarkdownText text={text} inline />
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function ActionItemsCard({ items }: { items: string[] }) {
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-3">
                Recommended action items
            </div>
            <ul className="flex flex-col gap-2.5">
                {items.map((text, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                        <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
                        <span className="text-[13px] text-gray-700 leading-snug">
                            <MarkdownText text={text} inline />
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function AnalysisProseCard({ text }: { text: string }) {
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-3">
                Situational analysis
            </div>
            <div
                className="text-[13px] leading-relaxed text-gray-700"
                style={{ textWrap: 'pretty' }}
            >
                <MarkdownText text={text} />
            </div>
        </div>
    );
}

function SourcesCard() {
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-3">
                Sources analyzed
            </div>
            <div className="flex flex-col gap-2">
                {ANALYSIS_SOURCES.map((s) => (
                    <div key={s.label} className="flex items-center gap-2 text-gray-600">
                        <s.icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <span className="text-[12px] font-medium text-gray-700 leading-snug">
                            {s.label}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

interface HistoryDropdownProps {
    runs: PatientAnalysis[];
    selectedId: string;
    onSelect: (id: string) => void;
    onClose: () => void;
}

function HistoryDropdown({ runs, selectedId, onSelect, onClose }: HistoryDropdownProps) {
    return (
        <>
            <div className="fixed inset-0 z-20" onClick={onClose} />
            <div className="absolute right-0 top-11 z-30 w-[300px] bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden">
                <div className="px-3.5 py-2.5 border-b border-gray-100 text-[10px] uppercase tracking-wider font-semibold text-gray-400">
                    Previous analyses
                </div>
                <div className="max-h-[320px] overflow-auto py-1">
                    {runs.map((run, index) => {
                        const theme = healthTheme(run.healthLabel);
                        return (
                            <button
                                key={run.id}
                                onClick={() => {
                                    onSelect(run.id);
                                    onClose();
                                }}
                                className={`w-full flex items-center gap-3 px-3.5 py-2.5 hover:bg-gray-50 text-left ${
                                    run.id === selectedId ? 'bg-gray-50' : ''
                                }`}
                            >
                                <span
                                    className={`w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-bold flex-shrink-0 ${
                                        run.status === 'error'
                                            ? 'bg-red-50 text-red-600'
                                            : `${theme.bg} ${theme.text}`
                                    }`}
                                >
                                    {run.status === 'success'
                                        ? (run.healthScore ?? '-')
                                        : run.status === 'error'
                                          ? '!'
                                          : '…'}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[13px] font-semibold text-gray-800 flex items-center gap-1.5">
                                        {format(new Date(run.createdAt), 'MMM d, yyyy')}
                                        {index === 0 && (
                                            <span className="text-[9px] font-bold uppercase tracking-wide px-1 py-0.5 rounded bg-gray-900 text-white">
                                                Latest
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-gray-400 flex items-center gap-1.5">
                                        {format(new Date(run.createdAt), 'h:mm a')}
                                        {run.status === 'success' ? (
                                            <span
                                                className={`inline-flex items-center gap-1 ${theme.text}`}
                                            >
                                                <span
                                                    className={`w-1 h-1 rounded-full ${theme.dot}`}
                                                />
                                                {theme.label}
                                            </span>
                                        ) : (
                                            <span
                                                className={
                                                    run.status === 'error'
                                                        ? 'text-red-500'
                                                        : 'text-amber-600'
                                                }
                                            >
                                                {run.status === 'error' ? 'Failed' : 'Pending'}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </>
    );
}

function durationSec(analysis: PatientAnalysis): number | null {
    if (!analysis.completedAt) return null;
    const ms = new Date(analysis.completedAt).getTime() - new Date(analysis.createdAt).getTime();
    return ms > 0 ? Math.round(ms / 1000) : null;
}

interface AnalysisResultViewProps {
    analysis: PatientAnalysis;
    runs: PatientAnalysis[];
    isLatest: boolean;
    onSelectRun: (id: string | null) => void;
    onRerun: () => void;
    isRerunning: boolean;
    /** 'slim': single-column stacked layout for narrow containers. */
    variant?: AnalysisVariant;
}

function AnalysisResultView({
    analysis,
    runs,
    isLatest,
    onSelectRun,
    onRerun,
    isRerunning,
    variant = 'full'
}: AnalysisResultViewProps) {
    const [historyOpen, setHistoryOpen] = useState(false);
    const generatedAt = analysis.completedAt ?? analysis.createdAt;
    const duration = durationSec(analysis);
    const slim = variant === 'slim';

    return (
        <div className={slim ? 'p-4 flex flex-col gap-3' : 'p-5 flex flex-col gap-4'}>
            {/* Toolbar */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="flex items-center gap-2">
                        <SparklesIcon className="w-4 h-4 text-gray-500" />
                        <h2 className="text-sm font-bold text-gray-900">Situational analysis</h2>
                        {!isLatest && (
                            <button
                                onClick={() => onSelectRun(null)}
                                className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors"
                                title="You are viewing an older snapshot - click to jump back to the latest run"
                            >
                                Snapshot · back to latest
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-400">
                        <ClockIcon className="w-3.5 h-3.5" />
                        <span>
                            Generated {format(new Date(generatedAt), 'MMM d, yyyy · h:mm a')}
                        </span>
                        {duration !== null && (
                            <>
                                <span className="text-gray-300">·</span>
                                <span>{duration}s</span>
                            </>
                        )}
                    </div>
                </div>
                <div className="relative flex items-center gap-2 flex-shrink-0">
                    <button
                        onClick={() => setHistoryOpen((open) => !open)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-[12px] font-semibold hover:bg-gray-50 transition-colors"
                    >
                        <ClockIcon className="w-3.5 h-3.5" /> History
                        <ChevronDownIcon
                            className={`w-3 h-3 transition-transform ${historyOpen ? 'rotate-180' : ''}`}
                        />
                    </button>
                    <button
                        onClick={onRerun}
                        disabled={isRerunning}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-gray-900 text-white text-[12px] font-semibold hover:bg-black shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <ArrowPathIcon className="w-3.5 h-3.5" />
                        {isRerunning ? 'Starting…' : 'Re-run'}
                    </button>
                    {historyOpen && (
                        <HistoryDropdown
                            runs={runs}
                            selectedId={analysis.id}
                            onSelect={(id) => onSelectRun(id)}
                            onClose={() => setHistoryOpen(false)}
                        />
                    )}
                </div>
            </div>

            {slim ? (
                /* Slim layout: everything stacks in one column */
                <>
                    <HealthScoreCard analysis={analysis} variant="slim" />
                    <TldrCard analysis={analysis} />
                </>
            ) : (
                /* Score + TL;DR + sources, 20/60/20 */
                <div className="grid gap-4" style={{ gridTemplateColumns: '1fr 3fr 1fr' }}>
                    <HealthScoreCard analysis={analysis} variant="full" />
                    <TldrCard analysis={analysis} />
                    <SourcesCard />
                </div>
            )}

            {/* Recap + Action items */}
            <div className={`grid gap-4 ${slim ? 'grid-cols-1' : 'grid-cols-2'}`}>
                <RecapCard items={analysis.result?.recap ?? []} />
                <ActionItemsCard items={analysis.result?.action_items ?? []} />
            </div>

            <AnalysisProseCard text={analysis.result?.analysis ?? ''} />

            {slim && <SourcesCard />}

            <div className="flex items-center gap-1.5 text-[11px] text-gray-400 pb-1">
                <SparklesIcon className="w-3 h-3" />
                AI-generated from the sources above. Review before acting - not a substitute for
                clinical judgment.
            </div>
        </div>
    );
}

export { AnalysisResultView };
